import clsx from "clsx";
import { Search } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { Link } from "wouter";
import { AddPicker } from "../../web/components/AddPicker";
import { ChoiceField, TextField } from "../../web/components/fields";
import { LeaveGuard } from "../../web/components/LeaveGuard";
import { ErrorNotice, Notice, Panel, Spinner } from "../../web/components/ui";
import { useCompany } from "../../web/lib/company";
import { count } from "../../web/lib/format";
import { plural } from "../../shared/words";
import { can, itemName, useJobTimeDraftPreview, useJobTimePreview, usePriceListSaver } from "./data";
import { duration } from "./describe";
import { type GroupJobTime, inUse, type ItemJobTime, JOB_TIME_DEFAULTS, type JobTimePreview, type JobTimeSettings as Settings, type PriceListData } from "./meta";
import { usePriceListWindows } from "./windows";

/*
 * Price List settings › Job time: which jobs, and how, work out how long items take (jobTime.ts), with a preview of what they give
 * (every item's time, the most used first, or any found by searching) to see at a glance whether they're believable. Changes are a
 * draft until saved: the preview then shows what they'd give beside what the saved settings do (was → now), to try them first.
 */

/** Items the preview lists before a search (the most used). */
const LISTED = 25;
/** ...and at most, searching. */
const FOUND = 100;
/** Customers with the most jobs listed to leave out (others by searching). */
const TOP_CUSTOMERS = 6;

/** Minutes, to the minute ("25 min", "1 hr 5 min"). */
const minutes = (n: number) => duration(Math.round(n));

const wholeAtLeast = (min: number, what: string) => (v: string) => (Number.isInteger(Number(v)) && Number(v) >= min ? null : `Enter ${what}`);

/** The settings back to their defaults, but not what's left out (a business's own choices, which the defaults can't know). */
const { leaveOutCustomers: _customers, leaveOutItems: _items, ...DEFAULT_SETTINGS } = JOB_TIME_DEFAULTS;
const atDefaults = (s: Settings) => Object.entries(DEFAULT_SETTINGS).every(([k, v]) => s[k as keyof Settings] === v);

/** The same settings, whatever order what's left out is in (one left out and taken out again isn't a change). */
function sameSettings(a: Settings, b: Settings) {
    const key = (s: Settings) =>
        JSON.stringify(Object.keys(JOB_TIME_DEFAULTS).map((k) => {
            if (k === "leaveOutItems") return s.leaveOutItems.toSorted();
            if (k === "leaveOutCustomers") return s.leaveOutCustomers.map((c) => c.uuid).toSorted();
            return s[k as keyof Settings];
        }));
    return key(a) === key(b);
}

/** A change to the draft: some settings, or worked out from the draft as it is then (two quick changes keep each other). */
type Edit = (patch: Partial<Settings> | ((t: Settings) => Partial<Settings>)) => Promise<unknown>;

export function JobTimeSettings({ data }: Readonly<{ data: PriceListData }>) {
    const saver = usePriceListSaver();
    const saved = data.settings.jobTime;
    // Changes not saved yet (null: none), and saving them.
    const [draft, setDraft] = useState<Settings | null>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const s = draft ?? saved;
    // Changed and different from what's saved (changes undone by hand count as none).
    const changed = draft !== null && !sameSettings(draft, saved);
    const disabled = !can(data, "settings") || busy;
    const seesStats = can(data, "stats");
    const savedPreview = useJobTimePreview(seesStats);
    const draftPreview = useJobTimeDraftPreview(seesStats && changed ? draft : null, savedPreview.dataUpdatedAt);
    const edit: Edit = (patch) => {
        setDraft((d) => {
            const base = d ?? saved;
            return { ...base, ...(typeof patch === "function" ? patch(base) : patch) };
        });
        setError(null);
        return Promise.resolve();
    };
    const save = () => {
        if (!draft) return;
        setBusy(true);
        setError(null);
        saver
            .saveJobTime(() => draft)
            .then(() => setDraft(null))
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    // What's shown: the draft's, once it's worked out (till then, the saved settings').
    const shown = changed ? (draftPreview.data ?? savedPreview.data) : savedPreview.data;
    return (
        <>
            {/* Not clipped (as panels are, for their corners): the searches' results drop down past its foot. */}
            <Panel
                title="Job time"
                id="job-time"
                className="overflow-visible!"
                actions={
                    // Into the draft (Save or Cancel it as any change), keeping what's left out.
                    <button
                        type="button"
                        className="btn-secondary px-2.5 py-1 text-xs"
                        disabled={disabled || atDefaults(s)}
                        title="Every setting back to how it starts. What you've left out stays. Nothing's saved until you save."
                        onClick={() => void edit(DEFAULT_SETTINGS)}
                    >
                        Back to defaults
                    </button>
                }
            >
                <p className="m-0 mb-4 text-sm text-muted">
                    How long items take, on each item&apos;s Stats tab: every completed job&apos;s checked-in time, shared out between the items on it.
                </p>
                <div className="grid items-start gap-4 sm:grid-cols-2">
                    <TextField
                        label="Look back"
                        value={String(s.lookBackMonths)}
                        suffix="months"
                        inputMode="numeric"
                        disabled={disabled}
                        validate={wholeAtLeast(1, "a number of months")}
                        hint="Only jobs from this far back count."
                        onSave={(v) => edit({ lookBackMonths: Number(v) })}
                    />
                    <ChoiceField
                        label="Drive time"
                        value={s.countDrive ? "in" : "out"}
                        disabled={disabled}
                        options={[
                            { value: "in", label: "Counted", hint: "Checking in starts with the drive there, so it's part of each job's time." },
                            { value: "out", label: "Taken off", hint: "ServiceM8's estimated drive is taken off each job's time." },
                        ]}
                        onSave={(v) => edit({ countDrive: v === "in" })}
                    />
                    <div className="grid grid-cols-2 gap-4">
                        <TextField
                            label="Shortest job"
                            value={String(s.minMinutes)}
                            suffix="min"
                            inputMode="numeric"
                            disabled={disabled}
                            validate={(v) => wholeAtLeast(0, "a number of minutes")(v) ?? (Number(v) < s.maxMinutes ? null : "Shorter than the longest")}
                            hint="Shorter is a mistake."
                            onSave={(v) => edit({ minMinutes: Number(v) })}
                        />
                        <TextField
                            label="Longest job"
                            value={String(s.maxMinutes / 60)}
                            suffix="hours"
                            inputMode="decimal"
                            disabled={disabled}
                            validate={(v) => (Number(v) > 0 && Number(v) * 60 > s.minMinutes && Number(v) <= 168 ? null : "Enter hours, over the shortest")}
                            hint="Longer is a mistake."
                            onSave={(v) => edit({ maxMinutes: Math.round(Number(v) * 60) })}
                        />
                    </div>
                    <ChoiceField
                        label="Archived items"
                        value={s.leaveOutArchived ? "out" : "in"}
                        disabled={disabled}
                        options={[
                            { value: "in", label: "Counted", hint: "Timed like any other item." },
                            { value: "out", label: "Left out", hint: "Jobs with an archived item on don't count." },
                        ]}
                        onSave={(v) => edit({ leaveOutArchived: v === "out" })}
                    />
                    <TextField
                        label="Jobs an item needs"
                        value={String(s.minJobs)}
                        suffix="jobs"
                        inputMode="numeric"
                        disabled={disabled}
                        validate={(v) => wholeAtLeast(1, "at least 1 job")(v) ?? (Number(v) <= 10_000 ? null : "At most 10,000 jobs")}
                        hint="Before it has a time of its own (and with more than one, for each extra). Fewer give rougher times."
                        onSave={(v) => edit({ minJobs: Number(v) })}
                    />
                    <ChoiceField
                        label="Several people on a job"
                        value={s.severalPeople}
                        disabled={disabled}
                        options={[
                            { value: "once", label: "Counted once", hint: "The time anyone was there: people there together count once." },
                            { value: "added", label: "Added up", hint: "Each person's time counts (person-time)." },
                        ]}
                        onSave={(v) => edit({ severalPeople: v })}
                    />
                </div>
                <div className="mt-6 grid items-start gap-6 lg:grid-cols-2">
                    <LeaveOutCustomers settings={s} preview={shown} disabled={disabled} seesStats={seesStats} onSave={edit} />
                    <LeaveOutItems data={data} settings={s} disabled={disabled} onSave={edit} />
                </div>
                {changed ? <SaveBar busy={busy} seesStats={seesStats} onSave={save} onCancel={() => setDraft(null)} /> : null}
                <div className="mt-3">
                    <ErrorNotice error={error} />
                </div>
                <LeaveGuard when={changed && !busy} message="Your changes to job time aren't saved yet." />
            </Panel>
            {seesStats ? <JobTimePreviewPanel data={data} settings={s} saved={savedPreview} draft={changed ? draftPreview : null} /> : null}
        </>
    );
}

/** Changes not saved yet: save them, or put the settings back. */
function SaveBar(props: Readonly<{ busy: boolean; seesStats: boolean; onSave: () => void; onCancel: () => void }>) {
    return (
        <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-md border border-brand-500/40 bg-brand-500/5 p-3">
            <p className="m-0 min-w-0 flex-1 text-sm text-muted">{props.seesStats ? "Not saved yet: the preview below shows what they'd change." : "Not saved yet."}</p>
            <div className="flex shrink-0 items-center gap-2">
                {props.busy ? <Spinner className="size-4" /> : null}
                <button type="button" className="btn-primary" disabled={props.busy} onClick={props.onSave}>
                    Save job time
                </button>
                <button type="button" className="btn-secondary" disabled={props.busy} onClick={props.onCancel}>
                    Cancel
                </button>
            </div>
        </div>
    );
}

/**
 * A list of what's left out, each with its own Take out, then how to add one (`add`). None left out: just how to add one, or with
 * nothing to add, a box saying there's none (where the list or the picker would be).
 */
function LeftOut({ title, hint, empty, rows, add }: Readonly<{ title: string; hint: string; empty: string; rows: { key: string; label: ReactNode; detail?: string; onTakeOut?: () => void }[]; add?: ReactNode }>) {
    return (
        <div className="space-y-2">
            <div>
                <h3 className="field-label m-0">{title}</h3>
                <p className="m-0 text-(length:--label-text) text-muted">{hint}</p>
            </div>
            {rows.length ? (
                <ul className="m-0 list-none divide-y divide-line rounded-md border border-line p-0">
                    {rows.map((r) => (
                        <li key={r.key} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                            <span className="min-w-0 flex-1 truncate">{r.label}</span>
                            {r.detail ? <span className="shrink-0 text-xs text-muted tabular-nums">{r.detail}</span> : null}
                            {r.onTakeOut ? (
                                <button type="button" className="btn-secondary px-2.5 py-1 text-xs" onClick={r.onTakeOut}>
                                    Take out
                                </button>
                            ) : null}
                        </li>
                    ))}
                </ul>
            ) : add ? null : (
                <p className="m-0 flex min-h-9 items-center rounded-md border border-dashed border-line px-3 py-1.5 text-sm text-muted">{empty}</p>
            )}
            {add}
        </div>
    );
}

/** "4,136 jobs". */
const jobsText = (n: number) => plural(n, "job");

/**
 * Customers whose jobs are left out (their work isn't like the rest, e.g. a contract's): those left out, then the customers with the
 * most jobs to leave out at a click, and a search for the rest. Their names need Clients synced (else they're listed unnamed).
 */
function LeaveOutCustomers(props: Readonly<{ settings: Settings; preview: JobTimePreview | undefined; disabled: boolean; seesStats: boolean; onSave: Edit }>) {
    const company = useCompany();
    const { settings: s, preview } = props;
    const out = new Set(s.leaveOutCustomers.map((c) => c.uuid));
    const jobsOf = new Map(preview?.customers.map((c) => [c.uuid, c.jobs]));
    const named = (c: { name: string }) => c.name || "Unnamed customer";
    const leaveOut = (c: { uuid: string; name: string }) =>
        props.onSave((t) => ({ leaveOutCustomers: [...t.leaveOutCustomers.filter((x) => x.uuid !== c.uuid), { uuid: c.uuid, name: c.name }] }));
    const takeOut = (uuid: string) => props.onSave((t) => ({ leaveOutCustomers: t.leaveOutCustomers.filter((c) => c.uuid !== uuid) }));
    const others = preview?.customers.filter((c) => !out.has(c.uuid)) ?? [];
    const noNames = preview?.clients.readiness === "off" || preview?.clients.readiness === "no-permission";
    const settings = <Link href="/settings/servicem8">Settings › ServiceM8</Link>;
    const turnOnClients = company.canManage ? <>To see customers&apos; names, turn on Clients in {settings}.</> : <>To see customers&apos; names, ask a manager to turn on Clients in {settings}.</>;
    let add: ReactNode = null;
    if (!props.disabled && props.seesStats && others.length && noNames)
        // Without names: the customers with the most jobs, to leave out at a click (a search needs names).
        add = (
            <div className="space-y-2">
                <p className="m-0 text-(length:--label-text) text-muted">Most jobs:</p>
                <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                    {others.slice(0, TOP_CUSTOMERS).map((c) => (
                        <li key={c.uuid}>
                            <button type="button" className="btn-secondary px-2.5 py-1 text-xs" title={`Leave out ${named(c)}'s jobs`} onClick={() => void leaveOut(c)}>
                                {named(c)} <span className="text-muted tabular-nums">· {count(c.jobs)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    else if (!props.disabled && props.seesStats && others.length)
        // With names: a search, the customers with the most jobs first (listed as soon as it's clicked into).
        add = (
            <AddPicker
                ranked
                placeholder="Leave out a customer: search, most jobs first"
                options={others.map((c) => ({ value: c.uuid, label: named(c), detail: jobsText(c.jobs) }))}
                onAdd={(uuid) => leaveOut(others.find((c) => c.uuid === uuid) ?? { uuid, name: "" })}
                onUndo={takeOut}
            />
        );
    return (
        <div className="space-y-3">
            <LeftOut
                title="Leave out customers"
                hint="Their jobs don't count, e.g. contract work that isn't like the rest."
                // Customers are picked from those with jobs that count: none yet, none to pick.
                empty={props.seesStats && !preview?.jobs ? "None. Customers to leave out show here once jobs count." : "None: every customer's jobs count."}
                rows={s.leaveOutCustomers.map((c) => ({
                    key: c.uuid,
                    label: named(c),
                    detail: jobsOf.has(c.uuid) ? jobsText(jobsOf.get(c.uuid) ?? 0) : undefined,
                    onTakeOut: props.disabled ? undefined : () => void takeOut(c.uuid),
                }))}
                add={add}
            />
            {noNames && props.seesStats ? <p className="m-0 text-xs text-muted">{turnOnClients}</p> : null}
        </div>
    );
}

/**
 * Items not timed (their quantity isn't work, e.g. a discount): their jobs still count for the other items on them. With archived
 * items left out (their jobs don't count at all), archived ones (and deleted) aren't offered or listed: they're kept, for if
 * archived items count again.
 */
function LeaveOutItems(props: Readonly<{ data: PriceListData; settings: Settings; disabled: boolean; onSave: Edit }>) {
    const { data, settings: s } = props;
    const out = new Set(s.leaveOutItems);
    const items = new Map(data.materials.map((m) => [m.uuid, m]));
    const shows = (uuid: string) => !s.leaveOutArchived || items.get(uuid)?.active === true;
    const listed = s.leaveOutItems.filter(shows);
    const hidden = s.leaveOutItems.length - listed.length;
    const add = (uuid: string) => props.onSave((t) => ({ leaveOutItems: [...t.leaveOutItems.filter((u) => u !== uuid), uuid] }));
    const takeOut = (uuid: string) => props.onSave((t) => ({ leaveOutItems: t.leaveOutItems.filter((u) => u !== uuid) }));
    return (
        <div className="space-y-3">
            <LeftOut
                title="Leave out items"
                hint="Not timed, e.g. a discount or an amount: their jobs still count for the other items."
                empty={hidden ? "None besides archived ones." : "None: every item is timed."}
                rows={listed.map((uuid) => {
                    const m = items.get(uuid);
                    return {
                        key: uuid,
                        label: (
                            <span className="flex min-w-0 items-center gap-1.5">
                                <span className="truncate">{m ? itemName(data, m) : "A deleted item"}</span>
                            </span>
                        ),
                        detail: m?.item_number || undefined,
                        onTakeOut: props.disabled ? undefined : () => void takeOut(uuid),
                    };
                })}
                add={
                    props.disabled ? null : (
                        <AddPicker
                            placeholder="Leave out an item: search by name or item number"
                            options={data.materials
                                .filter((m) => !out.has(m.uuid) && shows(m.uuid))
                                .map((m) => ({ value: m.uuid, label: itemName(data, m), detail: m.item_number || undefined }))}
                            onAdd={add}
                            onUndo={takeOut}
                        />
                    )
                }
            />
            {hidden ? (
                <p className="m-0 text-xs text-muted">
                    {plural(hidden, "archived item")} not shown: archived items are left out with their jobs.
                </p>
            ) : null}
        </div>
    );
}

// ---------------------------------------------------------------------------------------------
// The preview: what the settings give (a draft's beside the saved ones': was → now)
// ---------------------------------------------------------------------------------------------

/** The preview lists items, or groups (their items' times together). */
type View = "items" | "groups";

type Query = { data?: JobTimePreview; error: Error | null; isLoading: boolean; isFetching: boolean };

/** Minutes to the minute, or null: whether two differ as shown. */
const same = (a: number | null | undefined, b: number | null | undefined) => (a === null || a === undefined ? null : Math.round(a)) === (b === null || b === undefined ? null : Math.round(b));

/** A figure now, and what it was before the draft (when it was different): "15 min → 12 min". */
function Was<T extends number | null>({ now, was, show }: Readonly<{ now: T; was: T | undefined; show: (n: T) => ReactNode }>) {
    if (was === undefined || same(now, was)) return <>{show(now)}</>;
    return (
        <>
            <span className="font-normal text-muted">{show(was)} → </span>
            {show(now)}
        </>
    );
}

/**
 * What the settings give: how many jobs count and how well their items add up, then each item's time, or each group's (the most
 * used first, or those found), to scan for anything that isn't believable. A row opens it on its Stats tab. With a draft, what it'd
 * give, beside what the saved settings do.
 */
function JobTimePreviewPanel({ data, settings: s, saved, draft }: Readonly<{ data: PriceListData; settings: Settings; saved: Query; draft: Query | null }>) {
    const [search, setSearch] = useState("");
    const [view, setView] = useState<View>("items");
    const [onlyChanged, setOnlyChanged] = useState(false);
    // The draft's, once worked out (with the saved ones' to compare); else the saved ones'.
    const preview = draft?.data ?? saved.data;
    const before = draft?.data ? saved.data : undefined;
    const working = draft !== null && draft.isFetching;
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    let body: ReactNode;
    if (saved.isLoading) body = <Spinner />;
    else if (!preview) body = <ErrorNotice error={saved.error ?? new Error("The preview couldn't be loaded.")} />;
    else if (["off", "no-permission"].includes(preview.data.times.readiness)) body = <TimesOff preview={preview} />;
    else body = <PreviewSummary preview={preview} before={before} settings={s} />;
    const what = view === "items" ? "an item" : "a group";
    const compare = before !== undefined && onlyChanged;
    return (
        <Panel
            title="Job time preview"
            flush
            // Nothing to find with no jobs counting.
            actions={
                preview?.jobs ? (
                    <label className="relative block w-64 max-w-full">
                        <span className="sr-only">Find {what}</span>
                        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted" />
                        <input className="input pl-9" placeholder={`Find ${what}`} value={search} onChange={(e) => setSearch(e.target.value)} />
                    </label>
                ) : undefined
            }
        >
            {draft ? (
                <p className="m-0 flex items-center gap-2 px-5 pt-3 text-xs text-muted">
                    {working ? <Spinner className="size-3.5" /> : null}
                    {working ? "Working out your changes..." : "With your changes (not saved yet): what they change shows as was → now."}
                </p>
            ) : null}
            <div className={clsx("px-5 pt-3", !preview?.jobs && "pb-4")}>{body}</div>
            {preview?.jobs ? (
                <>
                    <nav className="tabs mt-3 items-center" aria-label="Show">
                        {(["items", "groups"] as const).map((v) => (
                            <button key={v} type="button" className="tab cursor-pointer" aria-current={view === v ? "page" : undefined} onClick={() => setView(v)}>
                                {v === "items" ? "Items" : "Groups"}
                            </button>
                        ))}
                        {before ? (
                            <label className="ml-auto flex cursor-pointer items-center gap-2 text-sm whitespace-nowrap text-muted">
                                <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
                                Only changed
                            </label>
                        ) : null}
                    </nav>
                    {view === "items" ? (
                        <ItemsTable data={data} settings={s} preview={preview} before={before} onlyChanged={compare} words={words} />
                    ) : (
                        <GroupsTable data={data} preview={preview} before={before} onlyChanged={compare} words={words} />
                    )}
                </>
            ) : null}
        </Panel>
    );
}

/** What's listed: those found ("12 found"), or the most used ("The 25 items on the most jobs"). */
function listedText(found: number, shown: number, searching: boolean, what: string) {
    const more = found > shown ? `: the first ${count(shown)} shown` : "";
    return searching ? `${count(found)} found${more}.` : `The ${count(shown)} ${what} on the most jobs. Find any other above.`;
}

/** The time columns' headings, after the first column's. */
function TimeTable({ first, children }: Readonly<{ first: string; children: ReactNode }>) {
    return (
        <table className="list is-fixed min-w-[560px]">
            <colgroup>
                <col className="w-[46%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[18%]" />
            </colgroup>
            <thead>
                <tr>
                    <th>{first}</th>
                    <th className="text-right">Jobs</th>
                    <th className="text-right">First one</th>
                    <th className="text-right">Each extra</th>
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    );
}

/** An item's or group's figures changed from \`was\` (one that's new or gone counts as changed). */
const differs = (now: { jobs: number; first: number | null; extra: number | null }, was: { jobs: number; first: number | null; extra: number | null } | undefined) =>
    was === undefined || now.jobs !== was.jobs || !same(now.first, was.first) || !same(now.extra, was.extra);

function ItemsTable(props: Readonly<{ data: PriceListData; settings: Settings; preview: JobTimePreview; before: JobTimePreview | undefined; onlyChanged: boolean; words: string[] }>) {
    const { data, settings: s, preview, before, words } = props;
    const windows = usePriceListWindows();
    const items = useMemo(() => new Map(data.materials.map((m) => [m.uuid, m])), [data.materials]);
    const was = useMemo(() => new Map(before?.items.map((i) => [i.uuid, i])), [before]);
    const leftOut = new Set(s.leaveOutItems);
    const rows = preview.items.filter((i) => {
        if (props.onlyChanged && !differs(i, was.get(i.uuid))) return false;
        if (!words.length) return !leftOut.has(i.uuid);
        const m = items.get(i.uuid);
        return m !== undefined && words.every((w) => `${itemName(data, m)} ${m.item_number}`.toLowerCase().includes(w));
    });
    const shown = rows.slice(0, words.length ? FOUND : LISTED);
    return (
        <>
            <TimeTable first="Item">
                {shown.map((i) => (
                    <PreviewRow
                        key={i.uuid}
                        data={data}
                        item={i}
                        was={before ? (was.get(i.uuid) ?? null) : undefined}
                        leftOut={leftOut.has(i.uuid)}
                        onOpen={() => windows.set({ item: i.uuid, itemTab: "stats" })}
                    />
                ))}
            </TimeTable>
            <p className="m-0 px-5 py-3 text-xs text-muted">
                {props.onlyChanged && !rows.length ? "Nothing listed changes. " : null}
                {listedText(rows.length, shown.length, words.length > 0, props.onlyChanged ? "changed items" : "items")} Items on fewer than {count(s.minJobs)} jobs share one time
                {preview.rare === null ? "" : ` (${minutes(preview.rare)} each)`}; each extra one needs {count(s.minJobs)} jobs with more than one.
            </p>
        </>
    );
}

function GroupsTable(props: Readonly<{ data: PriceListData; preview: JobTimePreview; before: JobTimePreview | undefined; onlyChanged: boolean; words: string[] }>) {
    const { data, preview, before, words } = props;
    const windows = usePriceListWindows();
    const names = new Map(data.groups.map((g) => [g.id, g.name]));
    const was = useMemo(() => new Map(before?.groups.map((g) => [g.id, g])), [before]);
    const rows = preview.groups.filter(
        (g) => names.has(g.id) && (!props.onlyChanged || differs(g, was.get(g.id))) && words.every((w) => (names.get(g.id) ?? "").toLowerCase().includes(w)),
    );
    const shown = rows.slice(0, words.length ? FOUND : LISTED);
    return (
        <>
            <TimeTable first="Group">
                {shown.map((g) => (
                    <GroupTimeRow key={g.id} name={names.get(g.id) ?? ""} group={g} was={before ? (was.get(g.id) ?? null) : undefined} onOpen={() => windows.set({ group: g.id, groupTab: "stats" })} />
                ))}
            </TimeTable>
            <p className="m-0 px-5 py-3 text-xs text-muted">
                {props.onlyChanged && !rows.length ? "Nothing listed changes. " : null}
                {listedText(rows.length, shown.length, words.length > 0, props.onlyChanged ? "changed groups" : "groups")} A group&apos;s time is its timed items&apos; together, each counted by
                how often it&apos;s used.
            </p>
        </>
    );
}

/** A time, or "-" (muted) for none. */
const timeOrDash = (n: number | null) => (n === null ? <span className="text-muted">-</span> : minutes(n));

/**
 * A row's Jobs, First one and Each extra (with \`was\`: the saved settings' too, where they differ; null: it wasn't there), or
 * \`instead\` across the two times (e.g. "Too few jobs").
 */
function Figures({ now, was, instead }: Readonly<{ now: { jobs: number; first: number | null; extra: number | null }; was: { jobs: number; first: number | null; extra: number | null } | null | undefined; instead?: string }>) {
    const before = was ?? undefined;
    return (
        <>
            <td className="text-right tabular-nums">
                <Was now={now.jobs} was={was === null ? 0 : before?.jobs} show={count} />
            </td>
            {instead ? (
                <td colSpan={2} className="text-right text-muted">
                    {instead}
                </td>
            ) : (
                <>
                    <td className="text-right font-semibold tabular-nums">
                        <Was now={now.first} was={was === null ? null : before?.first} show={timeOrDash} />
                    </td>
                    <td className="text-right tabular-nums">
                        <Was now={now.extra} was={was === null ? null : before?.extra} show={timeOrDash} />
                    </td>
                </>
            )}
        </>
    );
}

/** A group: its name, how many of its items are timed and their range, and its items' times together. */
function GroupTimeRow({ name, group: g, was, onOpen }: Readonly<{ name: string; group: GroupJobTime; was: GroupJobTime | null | undefined; onOpen: () => void }>) {
    const range = g.fastest !== null && g.slowest !== null ? ` · ${minutes(g.fastest)} to ${minutes(g.slowest)}` : "";
    return (
        <tr className="relative hover:bg-surface-2 focus-within:bg-brand-500/5">
            <td>
                {/* The name button covers the whole row (after:inset-0): it opens the group on its Stats tab. */}
                <button type="button" className="block w-full min-w-0 cursor-pointer text-left outline-none after:absolute after:inset-0" onClick={onOpen}>
                    <span className="block truncate">{name}</span>
                    <span className="block truncate text-xs text-muted">
                        {count(g.timed)} of {count(g.items)} items timed{range}
                    </span>
                </button>
            </td>
            <Figures now={g} was={was} instead={g.first === null ? "Too few jobs" : undefined} />
        </tr>
    );
}

/** Job time isn't there (off, or not allowed): what to do. */
function TimesOff({ preview }: Readonly<{ preview: JobTimePreview }>) {
    const company = useCompany();
    const settings = <Link href="/settings/servicem8">Settings › ServiceM8</Link>;
    if (preview.data.times.readiness === "no-permission") return <Notice tone="warning">Reconnect ServiceM8 ({settings}) and allow reading the schedule to time items.</Notice>;
    return <Notice>{company.canManage ? <>To time items, turn on Job time in {settings}.</> : <>To time items, ask a manager to turn on Job time in {settings}.</>}</Notice>;
}

/** Jobs found but left out: "4,136 with their customers, 85 for archived items left out" (or "None left out"). */
function leftOutText(preview: JobTimePreview) {
    const parts = [
        preview.customerJobs ? `${count(preview.customerJobs)} with their customers` : "",
        preview.archivedJobs ? `${count(preview.archivedJobs)} for archived items` : "",
    ].filter(Boolean);
    return parts.length ? `${parts.join(", ")} left out` : "None left out";
}

/**
 * What the base time per visit is made of: the drive (ServiceM8's estimate, its usual one) and the rest, on site ("18 min drive + 16 min
 * on site"); with the drive taken off, it's all on site (the drive said beside it).
 */
function baseParts(preview: JobTimePreview, countDrive: boolean) {
    const { everyJob: base, drive } = preview;
    if (base === null || drive === null) return "before any item";
    if (!countDrive) return `on site; ${minutes(drive)} drive taken off`;
    // The drive estimated longer than the base worked out (it can be): it's said to be in it, not split.
    if (drive > base) return `includes ${minutes(drive)} drive`;
    return `${minutes(drive)} drive + ${minutes(base - drive)} on site`;
}

/** A figure: its label, the value large (with what it was before the draft, if different), and a line under it. */
function SummaryFigure({ label, value, was, sub, title }: Readonly<{ label: string; value: string; was?: string; sub: ReactNode; title?: string }>) {
    return (
        <div className="min-w-0" title={title}>
            <dt className="text-xs text-muted">{label}</dt>
            <dd className="m-0 truncate text-lg font-semibold tabular-nums">
                {value}
                {was !== undefined && was !== value ? <span className="ml-1.5 text-xs font-normal text-muted">was {was}</span> : null}
            </dd>
            <dd className="m-0 truncate text-xs text-muted">{sub}</dd>
        </div>
    );
}

/** How many jobs count, how long one usually takes and how much of that is its base time, and how close their items add up. */
function PreviewSummary({ preview, before, settings: s }: Readonly<{ preview: JobTimePreview; before: JobTimePreview | undefined; settings: Settings }>) {
    const loading = (["jobs", "lines", "times"] as const).some((k) => preview.data[k].readiness === "loading");
    if (!preview.jobs) return <p className="m-0 text-sm text-muted">No jobs count with these settings{loading ? " yet: still reading your job history from ServiceM8" : ""}.</p>;
    const time = (n: number | null) => (n === null ? "-" : minutes(n));
    const accuracy = (p: JobTimePreview) => (p.miss === null ? "-" : `± ${minutes(p.miss)}`);
    // How many left out, in short (which ones on hover).
    const out = preview.customerJobs + preview.archivedJobs;
    const leftOut = out ? `${count(out)} left out` : "None left out";
    return (
        <div className="space-y-2">
            <dl className="m-0 grid grid-cols-2 gap-4 sm:grid-cols-4">
                <SummaryFigure label="Jobs counted" value={count(preview.jobs)} was={before && count(before.jobs)} sub={leftOut} title={leftOutText(preview)} />
                <SummaryFigure
                    label="Typical job"
                    value={time(preview.typicalJob)}
                    was={before && time(before.typicalJob)}
                    sub={s.countDrive ? "start to finish, drive in" : "on site, drive taken off"}
                />
                <SummaryFigure
                    label="Base time per visit"
                    value={time(preview.everyJob)}
                    was={before && time(before.everyJob)}
                    sub={baseParts(preview, s.countDrive)}
                    title="Each visit, before any item: the drive (ServiceM8's estimate), then parking, walking in, talking and taking payment. A job on two days has two."
                />
                <SummaryFigure label="Accuracy" value={accuracy(preview)} was={before && accuracy(before)} sub="a job vs its base and items" />
            </dl>
            {loading ? <p className="m-0 text-xs text-muted">Still reading your job history from ServiceM8: these will change.</p> : null}
        </div>
    );
}

function PreviewRow({ data, item, was, leftOut, onOpen }: Readonly<{ data: PriceListData; item: ItemJobTime; was: ItemJobTime | null | undefined; leftOut: boolean; onOpen: () => void }>) {
    const m = data.materials.find((x) => x.uuid === item.uuid);
    let instead: string | undefined;
    if (leftOut) instead = "Left out";
    else if (item.first === null) instead = "Too few jobs";
    return (
        <tr className="relative hover:bg-surface-2 focus-within:bg-brand-500/5">
            <td>
                {/* The name button covers the whole row (after:inset-0): it opens the item on its Stats tab. */}
                <button type="button" className="block w-full min-w-0 cursor-pointer text-left outline-none after:absolute after:inset-0" onClick={onOpen}>
                    <span className="flex min-w-0 items-center gap-1.5">
                        <span className={clsx("truncate", !(m && inUse(m)) && "text-muted")}>{m ? itemName(data, m) : "A deleted item"}</span>
                    </span>
                </button>
            </td>
            <Figures now={item} was={was} instead={instead} />
        </tr>
    );
}
